import type { SiteLocale } from "@/content/schema";
import { homeSectionHref, localePath } from "@/content/index";

export type StatusKind = "not-found" | "error";

export type StatusCopy = {
  locale: SiteLocale;
  dir: "ltr" | "rtl";
  code: string;
  title: string;
  body: string;
  homeLabel: string;
  homeHref: string;
  projectLabel: string;
  projectHref: string;
  contactLabel: string;
  contactHref: string;
  resetLabel?: string;
  metaTitle: string;
};

const CONTACT_EMAIL = "info@xattir.com";

const copy = {
  en: {
    notFound: {
      code: "404",
      title: "Page not found",
      body: "This path is not part of the Xattir site. Head home, or start a project with us.",
      homeLabel: "Back home",
      projectLabel: "Start a project",
      contactLabel: CONTACT_EMAIL,
      metaTitle: "Page not found · Xattir",
    },
    error: {
      code: "Error",
      title: "Something went wrong",
      body: "We hit an unexpected snag. Try again, or head home and continue from there.",
      homeLabel: "Back home",
      projectLabel: "Start a project",
      contactLabel: CONTACT_EMAIL,
      resetLabel: "Try again",
      metaTitle: "Something went wrong · Xattir",
    },
  },
  ar: {
    notFound: {
      code: "404",
      title: "الصفحة غير موجودة",
      body: "هذا المسار ليس جزءا من موقع Xattir. عد الى الرئيسية، او ابدا مشروعا معنا.",
      homeLabel: "العودة للرئيسية",
      projectLabel: "ابدأ مشروعا",
      contactLabel: CONTACT_EMAIL,
      metaTitle: "الصفحة غير موجودة · Xattir",
    },
    error: {
      code: "خطأ",
      title: "حدث خطأ ما",
      body: "واجهنا خللا غير متوقع. حاول مرة اخرى، او عد الى الرئيسية وتابع من هناك.",
      homeLabel: "العودة للرئيسية",
      projectLabel: "ابدأ مشروعا",
      contactLabel: CONTACT_EMAIL,
      resetLabel: "حاول مرة اخرى",
      metaTitle: "حدث خطأ ما · Xattir",
    },
  },
} as const;

export function getStatusCopy(
  locale: SiteLocale,
  kind: StatusKind,
): StatusCopy {
  const block = copy[locale][kind === "not-found" ? "notFound" : "error"];
  return {
    locale,
    dir: locale === "ar" ? "rtl" : "ltr",
    code: block.code,
    title: block.title,
    body: block.body,
    homeLabel: block.homeLabel,
    homeHref: localePath(locale),
    projectLabel: block.projectLabel,
    projectHref: homeSectionHref(locale, "contact"),
    contactLabel: block.contactLabel,
    contactHref: `mailto:${CONTACT_EMAIL}`,
    resetLabel: "resetLabel" in block ? block.resetLabel : undefined,
    metaTitle: block.metaTitle,
  };
}
